import { Loader2, Receipt } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { type OrderHistoryItem, useOrderHistory } from "@/hooks/use-orders";
import { formatPrice } from "@/lib/format-price";

const FULFILLMENT_LABELS: Record<string, string> = {
  delivery: "orders.fulfillment.delivery",
  dine_in: "orders.fulfillment.dineIn",
  takeaway: "orders.fulfillment.takeaway",
};

function formatDate(value: string): string {
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(
    new Date(value),
  );
}

export default function OrderHistoryListPage() {
  const { t } = useTranslation();
  const historyQuery = useOrderHistory();

  if (historyQuery.isPending) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (historyQuery.isError) {
    return <div className="text-destructive">{(historyQuery.error as Error).message}</div>;
  }

  const orders = historyQuery.data?.orders ?? [];

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">{t("orders.history.title", "Mis pedidos")}</h1>

      {orders.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
            <Receipt className="h-8 w-8 text-muted-foreground" />
            <p className="text-muted-foreground">
              {t(
                "orders.history.empty",
                "Aún no tenés pedidos. Cuando hagas uno con tu cuenta, va a aparecer acá.",
              )}
            </p>
            <Button asChild variant="outline">
              <Link to="/account">{t("orders.history.back", "Volver a mi cuenta")}</Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <ul className="space-y-3">
          {orders.map((order) => (
            <li key={order.id}>
              <OrderHistoryCard order={order} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function OrderHistoryCard({ order }: { order: OrderHistoryItem }) {
  const { t } = useTranslation();
  const fulfillmentLabel = FULFILLMENT_LABELS[order.fulfillmentType] ?? "orders.fulfillment.dineIn";

  return (
    <Link to={`/account/orders/${order.id}`} className="block">
      <Card>
        <CardContent className="flex items-start justify-between gap-3 p-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="font-medium">#{order.dailyNumber}</span>
              <span className="text-muted-foreground text-sm">{order.organizationName}</span>
            </div>
            <p className="text-muted-foreground text-sm">{formatDate(order.createdAt)}</p>
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <Badge variant="secondary">
                {t(`orders.status.${order.status}`, {
                  defaultValue: order.status.replace("_", " "),
                })}
              </Badge>
              <Badge variant="outline">
                {t(fulfillmentLabel, { defaultValue: order.fulfillmentType.replace("_", " ") })}
              </Badge>
            </div>
          </div>
          <span className="font-semibold tabular-nums">
            {formatPrice(order.total, order.currency)}
          </span>
        </CardContent>
      </Card>
    </Link>
  );
}
